import React, { useState } from "react";
import { Button, Dropdown } from "@themesberg/react-bootstrap";
import {
  FiCopy,
  FiChevronDown,
  FiEye,
  FiLock,
  FiCreditCard,
  FiPower,
  FiCheck,
  FiAlertTriangle,
} from "react-icons/fi";

const AVATAR_COLORS = [
  "#1B3C6E",
  "#2E7D32",
  "#00838F",
  "#6A1B9A",
  "#F57C00",
  "#1565C0",
];

export const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN")}`;

export const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
};

export const getAvatarColor = (name) => {
  let hash = 0;
  const str = String(name || "");
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export default function IdCard({ item, onView, onManageAction }) {
  const [copied, setCopied] = useState(false);

  const handleCopyReferral = async () => {
    try {
      await navigator.clipboard.writeText(item.referral_number || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const isActive = String(item.status || "").toLowerCase() === "active";
  const statusClass = String(item.status || "inactive").toLowerCase();

  return (
    <div className="my-ids-id-card">
     
      <div className="my-ids-id-card-header">
        <div className="d-flex align-items-start gap-3">
          <div
            className="my-ids-avatar"
            style={{ backgroundColor: getAvatarColor(item.name) }}
          >
            {getInitials(item.name)}
          </div>
          <div className="flex-grow-1 min-width-0">
            <h6 className="mb-0 fw-bold text-truncate">{item.name || "N/A"}</h6>
            {item.placed_under?.name && (
              <small className="text-muted d-block">
                Placed under {item.placed_under.name}
              </small>
            )}
            <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
              <small className="text-muted">{item.custom_user_id}</small>
              <span className="text-muted">•</span>
              <span className={`my-ids-status-badge ${statusClass}`}>
                {item.status || "N/A"}
              </span>
            </div>
            {item.last_login_at && (
              <small className="text-muted d-block mt-1">
                Last active: {item.last_login_at}
              </small>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center gap-1 mt-2">
          <small className="text-muted">Referral:</small>
          <span className="fw-semibold small">{item.referral_number || "N/A"}</span>
          <button
            type="button"
            className="my-ids-copy-btn"
            title={copied ? "Copied!" : "Copy referral number"}
            onClick={handleCopyReferral}
          >
            {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
          </button>
          {copied && (
            <small className="text-success fw-semibold">Copied!</small>
          )}
        </div>
      </div>

      <div className="my-ids-id-card-body">
        <div className="my-ids-earnings-row">
          <div>
            <div className="my-ids-earnings-label">This Month</div>
            <div className="my-ids-earnings-value">
              {formatCurrency(item.current_month_earnings)}
            </div>
          </div>
          <div>
            <div className="my-ids-earnings-label">All Time</div>
            <div className="my-ids-earnings-value">
              {formatCurrency(item.total_earnings)}
            </div>
          </div>
        </div>

        <div className="my-ids-stats-row">
          <span>Network: {item.network_size ?? 0}</span>
          <span>Direct: {item.direct_referrals ?? 0}</span>
        </div>

        <div
          className={`my-ids-course-badge ${
            item.has_purchased_course ? "purchased" : "pending"
          }`}
        >
          {item.has_purchased_course ? (
            <>
              <FiCheck /> Has purchased course
            </>
          ) : (
            <>
              <FiAlertTriangle /> No course yet
            </>
          )}
        </div>
      </div>

       {!item.has_purchased_course && (
        <div className="my-ids-commission-warning">
          <FiAlertTriangle className="me-1" style={{ verticalAlign: "-2px" }} />
          Commission inactive — this ID hasn&apos;t purchased a course yet.
        </div>
      )}


      <div className="my-ids-id-card-footer">
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="my-ids-btn-outline flex-grow-1 d-inline-flex align-items-center justify-content-center gap-1"
            onClick={() => onView(item)}
          >
            <FiEye /> View
          </Button>

          <Dropdown align="end">
            <Dropdown.Toggle
              size="sm"
              variant="outline-primary"
              className="my-ids-btn-outline d-inline-flex align-items-center gap-1"
              disabled={String(item.status).toLowerCase() === "blocked"}
            >
              Manage <FiChevronDown size={14} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => onManageAction("password", item)}>
                <FiLock className="me-2" /> Change Password
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onManageAction("bank", item)}>
                <FiCreditCard className="me-2" /> Bank Details
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => onManageAction("toggle", item)}
                className={isActive ? "text-warning" : "text-success"}
              >
                <FiPower className="me-2" />
                {isActive ? "Deactivate" : "Activate"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
}
